import React, { useState } from 'react';
import { NavigateFunction, useNavigate, useLocation } from 'react-router-dom';
import {
    Button,
    Icon,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Stack,
    Tooltip,
    Typography,
    useMediaQuery,
} from '@mui/material';
import ArtistNavbarLogo from '../assets/ArtistNavbarLogo.svg';
import { AuthContextType, AuthStatus, useAuth } from '../auth/Auth';
import ProfileButton from './ProfileButton';
import {
    AdminPanelSettings,
    Event,
    Help,
    Key,
    LibraryMusic,
    Menu as MenuIcon,
    Person2,
    SatelliteAlt,
    ShoppingBag,
    VideoLibrary,
} from '@mui/icons-material';
import { QuestionAnswer } from '@mui/icons-material';

const Logo: React.FC<{ isTopMenu: boolean }> = ({ isTopMenu }) => {
    const navigate = useNavigate();
    return (
        <Button
            sx={{
                height: isTopMenu ? '180px' : '80px',
                mr: isTopMenu ? '30px' : 0,
            }}
        >
            <img
                src={ArtistNavbarLogo}
                style={{
                    maxHeight: '100%',
                }}
                alt=''
                onClick={() => navigate('/')}
            />
        </Button>
    );
};

interface MenuProps {
    isTopMenu: boolean;
    auth: AuthContextType;
    isCommunityPage: boolean;
}

interface NavbarMenuProps {
    isTopMenu: boolean;
    isCommunityPage: boolean;
}

interface NavbarItem {
    name: string;
    path?: string;
    icon: JSX.Element | null;
    onClick: () => void;
}

const COMMUNITY_AMBER = '#FFB300';
const COMMUNITY_BLUE = '#29B6F6';

const NavMenuItem: React.FC<{
    item: NavbarItem;
    handleClick: (func: () => void) => () => void;
}> = ({ item, handleClick }) => {
    return (
        <>
            <MenuItem key={item.name} onClick={handleClick(item.onClick)}>
                <ListItemIcon>{item.icon}</ListItemIcon>
                <Typography textAlign='center' sx={{ fontFamily: 'LemonMilk' }}>
                    {item.name}
                </Typography>
            </MenuItem>
        </>
    );
};

function HelpButton() {
    return (
        <Tooltip key='help' title='Help'>
            <IconButton key='help' sx={{ color: 'inherit' }} href='/help'>
                <Help />
            </IconButton>
        </Tooltip>
    );
}

function useNavbarItems(
    isTopMenu: boolean,
    handleClick: (func: () => void) => () => void,
    auth: AuthContextType,
    isCommunityPage: boolean,
) {
    const navigate = useNavigate();

    const breakpoints = {
        topMenu: {
            showAll: useMediaQuery('(min-width:1200px)'),
            hide1: useMediaQuery('(min-width: 1100px)'),
            hide2: useMediaQuery('(min-width:1000px)'),
            hide3: useMediaQuery('(min-width:890px)'),
            hide4: useMediaQuery('(min-width:700px)'),
            hide5: useMediaQuery('(min-width:700px)'),
        },
        bottomMenu: {
            showAll: useMediaQuery('(min-width:800px)'),
            hide1: useMediaQuery('(min-width:700px)'),
            hide2: useMediaQuery('(min-width:600px)'),
            hide3: useMediaQuery('(min-width:515px)'),
            hide4: useMediaQuery('(min-width:440px)'),
            hide5: useMediaQuery('(min-width:360px)'),
        },
    };

    const showHelp = useMediaQuery('(min-width:800px)');

    const { showAll, hide1, hide2, hide3, hide4, hide5 } =
        breakpoints[isTopMenu ? 'topMenu' : 'bottomMenu'];
    const showProfileDropdown = useMediaQuery('(min-width:800px)');

    const location = useLocation();
    const startItems = allStartItems(navigate, isTopMenu, auth);

    let startItemCount = 0;
    startItemCount = startItems.length;
    if (showAll) {
        startItemCount = startItems.length;
    } else if (hide1) {
        startItemCount = startItems.length - 1;
    } else if (hide2) {
        startItemCount = startItems.length - 2;
    } else if (hide3) {
        startItemCount = startItems.length - 3;
    } else if (hide4) {
        startItemCount = startItems.length - 4;
    } else if (hide5) {
        startItemCount = startItems.length - 5;
    } else {
        startItemCount = startItems.length - 6;
    }

    const shownStartItems: JSX.Element[] = startItems
        .slice(0, startItemCount)
        .map((item) => {
            const isActive = !!item.path && location.pathname.startsWith(item.path);
            return isTopMenu ? (
                <TopMenuStartItem
                    key={item.name}
                    item={item}
                    isCommunityPage={isCommunityPage}
                    isActive={isActive}
                />
            ) : (
                <BottomMenuStartItem
                    key={item.name}
                    item={item}
                    isCommunityPage={isCommunityPage}
                    isActive={isActive}
                />
            );
        });

    const menuItems: JSX.Element[] = startItems
        .slice(startItemCount)
        .map((item) => (
            <NavMenuItem key={item.name} item={item} handleClick={handleClick} />
        ));

    const endItems: JSX.Element[] = [];

    if (auth.user?.isAdmin) {
        menuItems.push(
            <NavMenuItem
                key='admin'
                item={{
                    name: 'Admin',
                    icon: <AdminPanelSettings color='secondary' />,
                    onClick: () => navigate('/admin'),
                }}
                handleClick={handleClick}
            />
        );
    }

    if (showProfileDropdown) {
        endItems.push(<ProfileButton key='profileDropdown' />);
    }

    if (showHelp) {
        endItems.push(HelpButton());
    } else {
        menuItems.push(
            <NavMenuItem
                key='help'
                item={{
                    name: 'Help',
                    icon: <Help color='secondary' />,
                    onClick: () => navigate('/help'),
                }}
                handleClick={handleClick}
            />
        );
    }

    return {
        startItems: shownStartItems,
        menuItems: menuItems,
        endItems: endItems,
    };
}

function allStartItems(
    navigate: NavigateFunction,
    isTopMenu: boolean,
    auth: AuthContextType
): NavbarItem[] {
    return [
        {
            name: 'Merch',
            icon: <ShoppingBag color='secondary' />,
            onClick: () => {
                window.open('https://www.moonfivetwoapparel.com/shop', '_blank');
            },
        },
        {
            name: 'Music',
            path: '/music',
            icon: <LibraryMusic color='secondary' />,
            onClick: () => navigate('/music'),
        },
        {
            name: 'ATWW',
            path: '/ATWW',
            icon: <Key color='secondary' />,
            onClick: () => navigate('/ATWW'),
        },
        {
            name: 'Q&A',
            path: '/the-source',
            icon: <QuestionAnswer color='secondary' />,
            onClick: () => navigate('/the-source'),
        },
        {
            name: 'ESS',
            path: '/ess',
            icon: <SatelliteAlt color='secondary' />,
            onClick: () => navigate('/ess'),
        },
        {
            name: isTopMenu ? 'On Tour' : 'Tour',
            path: '/events',
            icon: <Event color='secondary' />,
            onClick: () => navigate('/events'),
        },
        {
            name: 'Media',
            path: '/videos',
            icon: <VideoLibrary color='secondary' />,
            onClick: () => navigate('/videos'),
        },

        ...(!isTopMenu
            ? [
                  {
                      name: 'Profile',
                      path: '/profile',
                      icon: <Person2 color='secondary' />,
                      onClick: () => navigate('/profile'),
                  },
              ]
            : []),
    ];
}

const BottomMenuStartItem: React.FC<{
    item: NavbarItem;
    isCommunityPage: boolean;
    isActive: boolean;
}> = ({ item, isCommunityPage, isActive }) => {
    const hoverBg = isCommunityPage ? 'rgba(255, 179, 0, 0.12)' : 'rgba(139, 0, 0, 0.1)';
    const activeBg = isCommunityPage ? 'rgba(255, 179, 0, 0.2)' : 'rgba(139, 0, 0, 0.18)';
    const activeColor = isCommunityPage ? COMMUNITY_AMBER : undefined;

    return (
        <>
            <Button
                key={item.name}
                color='secondary'
                onClick={item.onClick}
                sx={{
                    flex: 1,
                    transition: 'background 0.15s, opacity 0.15s, color 0.3s',
                    color: isActive && isCommunityPage ? activeColor : undefined,
                    '&:hover': { background: hoverBg },
                    '&:active': { opacity: 0.65, background: activeBg },
                }}
            >
                <Stack alignItems='center' justifyContent='center' spacing={1}>
                    <Icon
                        color='inherit'
                        aria-label={item.name}
                        sx={{
                            height: '30px',
                            color: isActive && isCommunityPage ? activeColor : undefined,
                        }}
                    >
                        {item.icon}
                    </Icon>
                    <Typography
                        sx={{
                            fontFamily: 'LemonMilk',
                            color: isActive && isCommunityPage ? activeColor : undefined,
                            fontWeight: isActive ? 'bold' : undefined,
                        }}
                        variant='caption'
                    >
                        {item.name}
                    </Typography>
                </Stack>
            </Button>
        </>
    );
};

const TopMenuStartItem: React.FC<{
    item: NavbarItem;
    isCommunityPage: boolean;
    isActive: boolean;
}> = ({ item, isCommunityPage, isActive }) => {
    const hoverBg = isCommunityPage ? 'rgba(0, 0, 0, 0.15)' : 'rgba(139, 0, 0, 0.08)';
    const underlineColor = isCommunityPage
        ? isActive
            ? COMMUNITY_AMBER
            : 'rgba(255,255,255,0.7)'
        : undefined;

    return (
        <>
            <Button
                key={item.name}
                onClick={item.onClick}
                sx={{
                    color: 'inherit',
                    whiteSpace: 'nowrap',
                    fontFamily: 'LemonMilk',
                    position: 'relative',
                    fontWeight: isActive ? 'bold' : undefined,
                    transition: 'background 0.2s, font-weight 0.1s',
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        bottom: 6,
                        left: '50%',
                        transform: isActive
                            ? 'translateX(-50%) scaleX(1)'
                            : 'translateX(-50%) scaleX(0)',
                        transformOrigin: 'center',
                        width: '55%',
                        height: '2px',
                        background: underlineColor ?? 'currentColor',
                        borderRadius: '2px',
                        transition: 'transform 0.22s ease, background 0.3s ease',
                        boxShadow: isActive && isCommunityPage
                            ? `0 0 8px ${COMMUNITY_AMBER}aa`
                            : 'none',
                    },
                    '&:hover::after': {
                        transform: 'translateX(-50%) scaleX(1)',
                    },
                    '&:hover': { background: hoverBg },
                }}
                startIcon={item.icon}
            >
                {item.name}
            </Button>
        </>
    );
};

const TopMenu: React.FC<MenuProps> = ({ isTopMenu, auth, isCommunityPage }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (func: () => void) => {
        return () => {
            func();
            handleClose();
        };
    };

    const { startItems, menuItems, endItems } = useNavbarItems(
        isTopMenu,
        handleClick,
        auth,
        isCommunityPage,
    );

    return (
        <>
            <Logo isTopMenu={isTopMenu} />
            <Stack direction='row' sx={{ flexGrow: 1, marginBottom: '50px' }}>
                <Stack direction='row' spacing={2}>
                    {startItems}
                    {menuItems.length > 0 && (
                        <>
                            <Tooltip title='More'>
                                <IconButton
                                    data-cy='navbar-more-button'
                                    onClick={handleOpen}
                                    sx={{ color: 'inherit' }}
                                >
                                    <MenuIcon />
                                </IconButton>
                            </Tooltip>
                            <Menu
                                id='menu-appbar'
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={handleClose}
                            >
                                {menuItems}
                            </Menu>
                        </>
                    )}
                </Stack>
                {endItems}
            </Stack>
        </>
    );
};

const BottomMenu: React.FC<MenuProps> = ({ isTopMenu, auth, isCommunityPage }) => {
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleClick = (func: () => void) => {
        return () => {
            func();
            handleClose();
        };
    };

    const { startItems, menuItems, endItems } = useNavbarItems(
        isTopMenu,
        handleClick,
        auth,
        isCommunityPage,
    );

    return (
        <Stack
            spacing={1}
            direction='row'
            justifyContent='space-between'
            sx={{
                flexGrow: 1,
            }}
        >
            <Logo isTopMenu={isTopMenu} />
            {startItems}
            {menuItems.length > 0 && (
                <>
                    <Tooltip title='More'>
                        <IconButton onClick={handleOpen} sx={{ color: 'inherit' }}>
                            <MenuIcon />
                        </IconButton>
                    </Tooltip>
                    <Menu
                        id='menu-appbar'
                        anchorEl={anchorEl}
                        open={Boolean(anchorEl)}
                        onClose={handleClose}
                    >
                        {menuItems}
                    </Menu>
                </>
            )}
            {endItems}
        </Stack>
    );
};

const NavbarMenu: React.FC<NavbarMenuProps> = ({ isTopMenu, isCommunityPage }) => {
    const auth = useAuth();

    if (auth.status === AuthStatus.Loading) {
        return <Logo isTopMenu={isTopMenu} />;
    }

    if (isTopMenu) {
        return <TopMenu isTopMenu={isTopMenu} auth={auth} isCommunityPage={isCommunityPage} />;
    }
    return <BottomMenu isTopMenu={isTopMenu} auth={auth} isCommunityPage={isCommunityPage} />;
};

export default NavbarMenu;
